import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { ApiConfiguration as __Configuration } from '../api-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { BehaviorSubject, Observable, Observable as __Observable, forkJoin, of } from 'rxjs';
import { map as __map, filter as __filter, catchError } from 'rxjs/operators';
import { ActivityDtoBasePagingResponse, ActivitySearchParams, AllocationSearchParams, FreeProductsDtoResponse, ProductTrayDtoBasePagingResponse, PutTrayPayload, TrayDTO } from '../models/trays-dto';
import { TAB } from 'src/app/enums/products.constants';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class TraysService extends __BaseService {

  static readonly traysPath = '/Tray';
  static readonly extraTraysPath = '/ExtraTray';
  static readonly activitiesPath = '/Tray/activities';

  public showShowcasePictureDialog$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  getBasePath(isExtraTray?: boolean) {
    if (isExtraTray) {
      return `${this.rootUrl}${TraysService.extraTraysPath}`;
    }
    return `${this.rootUrl}${TraysService.traysPath}`;
  }

  /**
   * Ritorna formattati correttamente i parametri di ricerca per i vassoi
   * @param params
   * @returns
   */
  getTrayQueryParams(params: any) {
    let _params = this.newParams();
    if (typeof params.hasBookedForSale === 'boolean') _params = _params.set('hasBookedForSale', params.hasBookedForSale.toString());

    if (params?.Search) {
      _params = _params.append("Search", params.Search)
    }
    if (params?.FullText) {
      _params = _params.append("FullText", params.FullText)
    }
    if (params?.ReferenceDate) {
      _params = _params.append("ReferenceDate", params.ReferenceDate.format('YYYY-MM-DD'))
    }
    if (Array.isArray(params?.Trays)) {
      params?.Trays.forEach((tray: string) => {
        _params = _params.append("Trays", tray);
      });
    }
    if (Array.isArray(params?.ShowcaseCodes)) {
      params?.ShowcaseCodes.forEach((showcase: string) => {
        _params = _params.append("ShowcaseCodes", showcase);
      });
    }
    if (params?.SapCodes) {
      params?.SapCodes.split(" ").forEach((sapCode: string) => {
        _params = _params.append("SapCode", sapCode);
      });
    }
    if (params?.Limit) {
      _params = _params.append("Limit", params.Limit)
    }
    if (params?.Skip) {
      _params = _params.append("Skip", params.Skip)
    }
    return _params;
  }

  /**
   **GET - /api/bo/Tray/{id}
   * Get a tray by Id
   * @param id
   * @returns
   */
  getTray(id: number, isExtraTray?: boolean): Observable<TrayDTO> {
    const url = `${this.getBasePath(isExtraTray)}/${id}`;
    return this.http.get<TrayDTO>(url);
  }

  /**
   **DELETE - /api/bo/Tray/{id}
   * Delete a tray.
   * @param id
  */
  deleteTray(id: number | string, isExtraTray?: boolean): Observable<any> {
    const url = `${this.getBasePath(isExtraTray)}/${id}`;
    return this.http.delete<any>(url)
  }

  /**
   **GET - /api/bo/Tray/{idEvent}/list
   * Get a list of trays by IdEvent
   * @param idEvent
   * @param params
   * @returns
  */
  getTraysList(idEvent: number, params?: AllocationSearchParams, isExtraTray?: boolean): Observable<ProductTrayDtoBasePagingResponse> {
    const url = `${this.getBasePath(isExtraTray)}/${idEvent}/list`;
    return this.http.get<ProductTrayDtoBasePagingResponse>(url, { params: this.getTrayQueryParams(params) })
  }

  /**
   **GET - /api/bo/Tray/{idEvent}/freeProducts
   * Get a list of products that not are in tray
   * @param idEvent
   * @param params
   * @returns
   */
  getFreeProductsList(idEvent: number, params?: any, isExtraTray?: boolean): Observable<FreeProductsDtoResponse> {
    const url = `${this.getBasePath(isExtraTray)}/${idEvent}/freeProducts`;
    return this.http.get<FreeProductsDtoResponse>(url, { params: this.getTrayQueryParams(params) })
  }

  /**
   **POST - /api/bo/Tray
   * Create a tray.
   * @param idEvent
   * @param tray
   * @returns
   */
  createTray(idEvent: number, tray?: any, isExtraTray?: boolean): Observable<any> {
    const url = `${this.getBasePath(isExtraTray)}`;
    tray.idHero = idEvent;
    if (tray.referenceDate) {
      if (typeof tray.referenceDate === "string") {
        tray.referenceDate = moment(tray.referenceDate);
      }
      tray.referenceDate = tray.referenceDate.format('YYYY-MM-DD')
    }
    return this.http.post<any>(url, tray)
  }

  /**
   **PUT - /api/bo/Tray
   * Update a tray.
   * @param body
   * @returns
  */
  updateTray(body: PutTrayPayload, isExtraTray?: boolean): Observable<any> {
    const url = `${this.getBasePath(isExtraTray)}`;
    if (body.referenceDate) {
      if (typeof body.referenceDate === "string") {
        body.referenceDate = moment(body.referenceDate);
      }
      body.referenceDate = body.referenceDate.format('YYYY-MM-DD')
    }
    return this.http.put<any>(url, body)
  }

  /**
   **PUT - /api/bo/Tray/{idEvent}/AddProducts/{idTray}
   * Add a products in tray.
   * @param idEvent
   * @param idTray
   * @param sapCodes
   * @returns
   */
  moveProductsInTray(idEvent: number, idTray: number | string, sapCodes: string[], isExtraTray?: boolean): Observable<any> {
    const url = `${this.getBasePath(isExtraTray)}/${idEvent}/AddProducts/${idTray}`;
    return this.http.put<any>(url, sapCodes)
  }

  /**
   **DELETE - /api/bo/Tray/{idEvent}/products
   * Delete products from trays.
   * @param idEvent
   * @param sapCodes
   * @returns
   */
  deleteProducts(idEvent: number, sapCodes: string[], isExtraTray?: boolean, idExtraTray?: string | number): Observable<any> {
    const url = `${this.getBasePath(isExtraTray)}/${idEvent}/products`;
    let params = this.newParams();
    if (Array.isArray(sapCodes)) {
      sapCodes.forEach((country: string) => {
        params = params.append("sapCodes", country);
      });
    }
    if (isExtraTray && idExtraTray) {
      params = params.append("idExtraTray", idExtraTray);
    }
    return this.http.delete<any>(url, { params })
  }

  /**
   **POST - /api/bo/Tray/{idEvent}/upload
   * Import tray products for event.
   * @param idEvent
   * @param file
   * @returns
  */
  upload(idEvent: number, file: File, isExtraTray?: boolean): Observable<any> {
    const url = `${this.getBasePath(isExtraTray)}/${idEvent}/upload`;
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(url, formData)
  }

  /**
   **GET - /api/bo/Tray/export/{idEvent}
   **GET - /api/bo/Tray/export/{idEvent}/ppt
   * Export the excel or the ppt
   * @param idEvent
   * @param searchParams
   * @param ppt
   * @returns
  */
  export(idEvent: number, searchParams: any, ppt?: boolean, isExtraTray?: boolean): Observable<any> {
    let url = `${this.getBasePath(isExtraTray)}/export/${idEvent}`;
    let responseType = "blob";
    if (ppt) {
      url += "/ppt";
      //   responseType = 'text';
    }
    return this.http.get<any>(url, { params: this.getTrayQueryParams(searchParams), responseType: responseType as "json" })
  }

  exportActivities(idEvent: number, searchParams: any): Observable<any> {
    let url = `${this.rootUrl}/Tray/export/activities/${idEvent}`;
    return this.http.get<any>(url, { params: this.getTrayQueryParams(searchParams), responseType: 'blob' as "json" })
  }


  /**
   **GET - /api/bo/Tray/activities/todo/{idEvent}
   **GET - /api/bo/Tray/activities/done/{idEvent}
   * Get a list of activities to do
   *  @param idEvent
   *  @param params
   *  @param done
   *  @returns
  */
  getActivities(idEvent: number, params?: ActivitySearchParams, done?: boolean): Observable<ActivityDtoBasePagingResponse> {
    const url = `${this.rootUrl}${TraysService.activitiesPath}/${done ? 'done' : 'todo'}/${idEvent}`;
    let _params = this.newParams();
    if (params?.FullText) {
      _params = _params.append("FullText", params.FullText)
    }
    if (params?.Data) {
      _params = _params.append('Data', params.Data.toString());
    }
    if (params?.Limit) {
      _params = _params.append("Limit", params.Limit)
    }
    if (params?.Skip) {
      _params = _params.append("Skip", params.Skip)
    }
    return this.http.get<ActivityDtoBasePagingResponse>(url, { params: _params })
  }

  /**
   **PUT - /api/bo/Tray/activities/{id}
   * Set Activity in Done.
   * @param id
  */
  completeActivity(activities: number[]): Observable<any> {
    const url = `${this.rootUrl}${TraysService.activitiesPath}`;
    return this.http.put<any>(url, { id: activities })
  }

  isShowcaseAllocation(allocationType: string) {
    return allocationType === TAB.SHOWCASE;
  }

  isTrayAllocation(allocationType: string) {
    return allocationType === TAB.TRAY;
  }

  isExtraTrayAllocation(allocationType: string) {
    return allocationType === TAB.EXTRA_TRAY;
  }

  isBasketAllocation(allocationType: string) {
    return allocationType === TAB.BASKET;
  }

  isGenerallyTrayAllocation(allocationType: string) {
    return !this.isShowcaseAllocation(allocationType);
  }
}
